import { computed } from "vue";
import { cloneDeep } from "lodash-es";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useCompaniesStore } from "@/pinia/companiesStore";

/**
 * User Settings composable.
 *
 * Stores user preferences (filters, sort order, date ranges, toggles) with
 * company-scoped isolation.
 *
 * Storage strategy:
 * - Viewing OWN company: localStorage + backend DB (syncs across devices).
 * - Viewing OTHER company: localStorage only (isolated, temporary state).
 *
 * localStorage key format: `company_filters_company:{companyId}_app:{appId}`.
 */
export function useUserSettings() {
  const userStore = useUserStore();
  const appsStore = useAppsStore();
  const companiesStore = useCompaniesStore();

  const currentCompanyIdentifier = computed(
    () => companiesStore?.viewingCompanySlug || userStore?.currUser?.company_id || "",
  );

  const isViewingOwnCompany = computed(() => !companiesStore?.viewingCompanySlug);

  const storageKey = computed(() => {
    const appId = appsStore?.app?.id;
    const companyId = currentCompanyIdentifier.value;
    if (!companyId || !appId) return null;
    return `company_filters_company:${companyId}_app:${appId}`;
  });

  // Read fresh from localStorage on every call. A cached `computed` would not
  // re-evaluate after `localStorage.setItem` (localStorage is not reactive),
  // so two sequential `setUserSetting` calls with different keys would lose
  // the first write.
  const readAll = () => {
    if (!storageKey.value) return {};
    try {
      const stored = localStorage.getItem(storageKey.value);
      if (stored) return JSON.parse(stored);

      const appId = appsStore?.app?.id;
      if (appId && userStore?.currUser?.settings?.[appId]?.rf_filter_settings) {
        return userStore.currUser.settings[appId].rf_filter_settings;
      }
      return {};
    } catch (e) {
      console.error("Error reading settings:", e);
      return {};
    }
  };

  const valuesDifferent = (a, b) => {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return true;
      return a.some((v, i) => v !== b[i]);
    }
    return a !== b;
  };

  const persist = items => {
    if (!storageKey.value) return Promise.resolve(false);

    try {
      localStorage.setItem(storageKey.value, JSON.stringify(items));
    } catch (e) {
      console.error("Error saving settings to localStorage:", e);
    }

    if (isViewingOwnCompany.value && userStore?.currUser && appsStore?.app?.id) {
      try {
        const user = cloneDeep(userStore.currUser);
        if (!user.settings) user.settings = {};
        user.settings[appsStore.app.id] = { rf_filter_settings: items };
        return userStore.updateUserSettings(user);
      } catch (e) {
        console.error("Error saving settings to backend:", e);
        return Promise.resolve(false);
      }
    }

    return Promise.resolve(true);
  };

  const getUserSetting = key => readAll()[key];

  const setUserSetting = (key, value) => {
    const items = cloneDeep(readAll());
    if (!valuesDifferent(items[key], value)) return Promise.resolve(true);
    items[key] = value;
    return persist(items);
  };

  const setUserSettings = settings => {
    const items = cloneDeep(readAll());
    let changed = false;
    for (const key of Object.keys(settings)) {
      if (valuesDifferent(items[key], settings[key])) {
        items[key] = settings[key];
        changed = true;
      }
    }
    if (!changed) return Promise.resolve(true);
    return persist(items);
  };

  return { getUserSetting, setUserSetting, setUserSettings };
}
